/**
 * PATH: apps/hr/src/pages/inbox/InboxPage.tsx
 * FILE_ID: HR-INBOX-PAGE-001
 * PURPOSE: Supervisor approval queue (Phase 3 of HR_UNIFICATION_PLAN.md).
 *          Lists submitted timesheet days with confidence-aware
 *          amber/red sort. Supervisor can approve/reject one row,
 *          or bulk approve/reject many rows with a single action.
 *
 * WHY: The previous workflow had supervisors hand-checking punches in
 *      the worker-facing timesheet view. This page consolidates the
 *      triage into a single queue; the BE service stamps low
 *      confidence on out-of-geofence / no-GPS punches so the worst
 *      rows surface first.
 *
 * FLOW:
 *   ┌──────────┐   ┌──────────────────┐   ┌────────────────────┐
 *   │ on mount │──▶│ GET /timesheets/ │──▶│ render rows; each  │
 *   └──────────┘   │   inbox          │   │ has Approve/Reject │
 *                  └──────────────────┘   └────────────────────┘
 *                          ▲                       │
 *                          │                       ▼
 *                          │              ┌────────────────────┐
 *                          └──────────────│ POST approve/reject│
 *                                         │ + refresh()        │
 *                                         └────────────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/auth: useHrFeatures (gates on `approvals` flag)
 *   - apps/hr/src/lib/workforceApi: workforce-api fetch wrapper
 *   - ./inboxHelpers / ./inboxTypes / ./InboxSubcomponents
 *
 * RELATED:
 *   - workforce-api/app/api/routes/timesheets/days.py (BE endpoints)
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Inbox } from 'lucide-react'
import { useHrFeatures } from '@quotech/auth'
import { workforceApi } from '../../lib/workforceApi'
import { extractErrorMessage } from './inboxHelpers'
import {
  BulkActionBar,
  FeatureDisabledNotice,
  InboxEmpty,
  InboxRow,
} from './InboxSubcomponents'
import type {
  BulkActionResponse,
  InboxItem,
  InboxResponse,
  InboxStatus,
} from './inboxTypes'

const STATUS_TABS: { id: InboxStatus; label: string }[] = [
  { id: 'submitted', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
]

export default function InboxPage() {
  const { features, isLoading: featuresLoading } = useHrFeatures()

  if (featuresLoading) {
    return <div className="p-8 text-sm text-slate-500">Loading…</div>
  }
  if (!features.approvals) {
    return <FeatureDisabledNotice />
  }
  return <InboxPanel />
}

function InboxPanel() {
  const [statusFilter, setStatusFilter] = useState<InboxStatus>('submitted')
  const [items, setItems] = useState<InboxItem[]>([])
  const [total, setTotal] = useState(0)
  const [anomalyCount, setAnomalyCount] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setError(null)
    try {
      const data = await workforceApi.get<InboxResponse>(
        `/timesheets/inbox?status=${statusFilter}&limit=100`,
      )
      setItems(data.inbox ?? [])
      setTotal(data.total ?? 0)
      setAnomalyCount(data.anomaly_count ?? 0)
      setSelected(new Set())
    } catch (err) {
      setError(`Failed to load queue: ${extractErrorMessage(err)}`)
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    setLoading(true)
    void refresh()
  }, [refresh])

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === items.length
        ? new Set()
        : new Set(items.map((it) => it.id)),
    )
  }, [items])

  const handleSingleApprove = useCallback(
    async (dayId: string) => {
      setBusy(true)
      setError(null)
      setSuccess(null)
      try {
        await workforceApi.post(`/timesheets/days/${dayId}/approve`, {})
        setSuccess('Approved.')
        await refresh()
      } catch (err) {
        // The day can be saved even when the response fails; reload first,
        // because refresh() clears the error banner.
        await refresh()
        setError(`Approve failed: ${extractErrorMessage(err)}`)
      } finally {
        setBusy(false)
      }
    },
    [refresh],
  )

  const handleSingleReject = useCallback(
    async (dayId: string) => {
      const reason = window.prompt('Reason for rejection?')
      if (!reason || !reason.trim()) return
      setBusy(true)
      setError(null)
      setSuccess(null)
      try {
        await workforceApi.post(`/timesheets/days/${dayId}/reject`, {
          reason: reason.trim(),
        })
        setSuccess('Rejected.')
        await refresh()
      } catch (err) {
        await refresh()
        setError(`Reject failed: ${extractErrorMessage(err)}`)
      } finally {
        setBusy(false)
      }
    },
    [refresh],
  )

  const handleBulkApprove = useCallback(async () => {
    if (selected.size === 0) return
    setBusy(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await workforceApi.post<BulkActionResponse>(
        '/timesheets/inbox/bulk-approve',
        { day_ids: Array.from(selected) },
      )
      const approvedCount = result.approved_count ?? 0
      const skipped = result.skipped_count ?? 0
      setSuccess(
        `Approved ${approvedCount}${skipped > 0 ? ` (skipped ${skipped})` : ''}.`,
      )
      await refresh()
    } catch (err) {
      await refresh()
      setError(`Bulk approve failed: ${extractErrorMessage(err)}`)
    } finally {
      setBusy(false)
    }
  }, [selected, refresh])

  const handleBulkReject = useCallback(
    async (reason: string) => {
      if (selected.size === 0) return
      setBusy(true)
      setError(null)
      setSuccess(null)
      try {
        const result = await workforceApi.post<BulkActionResponse>(
          '/timesheets/inbox/bulk-reject',
          { day_ids: Array.from(selected), reason },
        )
        const rejectedCount = result.rejected_count ?? 0
        const skipped = result.skipped_count ?? 0
        setSuccess(
          `Rejected ${rejectedCount}${skipped > 0 ? ` (skipped ${skipped})` : ''}.`,
        )
        await refresh()
      } catch (err) {
        await refresh()
        setError(`Bulk reject failed: ${extractErrorMessage(err)}`)
      } finally {
        setBusy(false)
      }
    },
    [selected, refresh],
  )

  const allSelected = useMemo(
    () => items.length > 0 && selected.size === items.length,
    [items, selected],
  )

  return (
    <div className="max-w-4xl mx-auto p-4">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Inbox className="w-5 h-5 text-teal-600" />
            Approval queue
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Risky punches surface first. {total} {total === 1 ? 'day' : 'days'}
            {anomalyCount > 0 ? ` · ${anomalyCount} flagged` : ''}.
          </p>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                statusFilter === tab.id
                  ? 'bg-white text-teal-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="mb-3 px-3 py-2 text-sm rounded-md bg-rose-50 border border-rose-200 text-rose-800">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 px-3 py-2 text-sm rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">
          {success}
        </div>
      )}

      <BulkActionBar
        selectedCount={selected.size}
        busy={busy}
        onApprove={handleBulkApprove}
        onReject={handleBulkReject}
        onClear={() => setSelected(new Set())}
      />

      {loading ? (
        <div className="p-8 text-sm text-slate-500">Loading queue…</div>
      ) : items.length === 0 ? (
        <InboxEmpty status={statusFilter} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              aria-label="Select all"
              className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-xs text-slate-500">
              {allSelected ? 'All selected' : `${items.length} rows`}
            </span>
          </div>
          <ul role="list">
            {items.map((it) => (
              <InboxRow
                key={it.id}
                item={it}
                selected={selected.has(it.id)}
                busy={busy}
                onToggle={toggleRow}
                onApprove={handleSingleApprove}
                onReject={handleSingleReject}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
